import { graphql, useStaticQuery } from 'gatsby';
import React, { type FC } from 'react';
import Helmet from 'react-helmet';
import { usePageContext, useRouterContext } from '../../../hooks';
import { excludeSearchEngines } from '../../../utils/utils';
import type { SeoPropsType } from './types';

const SEO: FC<SeoPropsType> = ({ description, title }) => {
    const { lang, originalPath } = usePageContext();

    const { location } = useRouterContext();
    const { site } = useStaticQuery(query);
    const { defaultTitle, defaultDescription, siteUrl, supportedLanguages } = site.siteMetadata;
    const seo = {
        title: title || defaultTitle,
        description: description || defaultDescription,
        url: `${siteUrl}${location?.pathname.replace(/\/$/, '')}`,
    };

    return (
        <Helmet title={seo.title}>
            <html lang="en" />
            <link rel="canonical" href={seo.url} />
            <meta charSet="utf-8" />
            <meta httpEquiv="X-UA-Compatible" content="IE=edge,chrome=1" />

            {excludeSearchEngines && <meta name="robots" content="noindex" />}

            <title>{seo.title}</title>
            <meta name="description" content={seo.description} />
            <meta name="og:og:locale" content={lang} />

            {/* <!-- Google / Search Engine Tags --> */}
            <meta property="name" content={seo.title} />
            <meta property="description" content={seo.description} />
            <meta property="image" content="https://learn.trades.org/images/preview.png" />

            {/* <!-- Facebook Meta Tags --> */}
            <meta property="og:url" content={seo.url} />
            <meta property="og:type" content="website" />
            <meta property="og:title" content={seo.title} />
            <meta property="og:description" content={seo.description} />
            <meta property="og:image" content="https://learn.trades.org/images/preview.png" />

            {/* <!-- Twitter Meta Tags --> */}
            <meta property="twitter:card" content="summary_large_image" />
            <meta property="twitter:title" content={seo.title} />
            <meta property="twitter:description" content={seo.description} />
            <meta property="twitter:image" content="https://learn.trades.org/images/preview.png" />

            <link rel="canonical" href="/favicon/favicon-192.png" sizes="192x192" />
            <link rel="alternate" hrefLang="x-default" href={`${seo.url}${originalPath}`} />

            {supportedLanguages.map((language, index) => (
                <link key={`leanguage-${index + 1}`} rel="alternate" hrefLang="x-default" href={`${seo.url}/${language}${originalPath}`} />
            ))}

            <link rel="icon" href="/favicon/favicon-32.png" sizes="32x32" />
            <link rel="icon" href="/favicon/favicon-192.png" sizes="192x192" />
            <link rel="apple-touch-icon" href="/favicon/favicon-152.png" sizes="152x152" />
            <link rel="apple-touch-icon" href="/favicon/favicon-167.png" sizes="167x167" />
            <link rel="apple-touch-icon" href="/favicon/favicon-180.png" sizes="180x180" />
            <link rel="shortcut icon" href="/favicon/favicon-196.png" sizes="196x196" />
        </Helmet>
    );
};

const query = graphql`
  query SEO {
    site {
      siteMetadata {
        defaultTitle: title
        defaultDescription: description
        siteUrl
        supportedLanguages
      }
    }
  }
`;

export default SEO;
